import React, { useEffect, useState, useCallback } from 'react';
import {
  Typography, Button, TextField, Alert, Box, Paper, Slider,
  FormControl, InputLabel, Select, MenuItem,
} from '@mui/material';
import { Save, VolumeUp } from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';

const VOICES = [
  { value: 'en_US-lessac-medium', label: 'Lessac (US English, Medium)' },
  { value: 'en_US-lessac-high', label: 'Lessac (US English, High)' },
  { value: 'en_US-amy-medium', label: 'Amy (US English, Female, Medium)' },
  { value: 'en_US-hfc_female-medium', label: 'HFC Female (US English, Female, Medium) — smooth/even' },
  { value: 'en_US-kristin-medium', label: 'Kristin (US English, Female, Medium) — calm' },
  { value: 'en_US-ljspeech-high', label: 'LJSpeech (US English, Female, High)' },
  { value: 'en_US-ryan-medium', label: 'Ryan (US English, Medium)' },
  { value: 'en_US-kusal-medium', label: 'Kusal (US English, Medium)' },
  { value: 'en_GB-alan-medium', label: 'Alan (British English, Medium)' },
];

export default function TTSSettingsPage() {
  const { tenantId, role } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(true);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [engine, setEngine] = useState('piper');
  const [voice, setVoice] = useState('en_US-lessac-medium');
  const [rate, setRate] = useState(1.0);
  const [pitch, setPitch] = useState(1.0);
  const [volume, setVolume] = useState(1.0);
  const [repeatCount, setRepeatCount] = useState(2);
  const [template, setTemplate] = useState('Attention {station}. {call_type} at {address}. {units} respond.');
  const [previewText, setPreviewText] = useState('Attention Station 1. Structure fire at 123 Main Street. Engine 1, Truck 1 respond.');

  const isAdmin = role === 'admin';

  const load = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    try {
      const res = await api.get(`/api/tenants/${tenantId}/tts-settings`);
      const d = res.data;
      setEngine(d.engine);
      setVoice(d.voice);
      setRate(d.rate);
      setPitch(d.pitch);
      setVolume(d.volume);
      setRepeatCount(d.repeat_count);
      setTemplate(d.announcement_template);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load settings');
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async () => {
    if (!tenantId) return;
    try {
      await api.put(`/api/tenants/${tenantId}/tts-settings`, {
        engine, voice, rate, pitch, volume, repeat_count: repeatCount,
        announcement_template: template,
      });
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Save failed');
    }
  };

  const handlePreview = async () => {
    if (!tenantId) return;
    setPreviewLoading(true);
    setError(null);
    try {
      const res = await api.post(
        `/api/tenants/${tenantId}/tts-settings/preview`,
        { text: previewText, rate, pitch, volume, voice },
        { responseType: 'blob' }
      );
      const blob = new Blob([res.data], { type: 'audio/wav' });
      const audioUrl = URL.createObjectURL(blob);
      const audio = new Audio(audioUrl);
      audio.onended = () => URL.revokeObjectURL(audioUrl);
      audio.onerror = () => {
        setError('Audio playback failed');
        URL.revokeObjectURL(audioUrl);
      };
      await audio.play();
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      if (detail) {
        setError(`TTS preview failed: ${detail}`);
      } else if (err.name === 'NotAllowedError') {
        setError('Browser blocked audio playback. Click the page first, then try again.');
      } else {
        setError(`TTS preview failed: ${err.message || 'Unknown error'}`);
      }
    } finally {
      setPreviewLoading(false);
    }
  };

  if (loading) return <Typography>Loading...</Typography>;

  return (
    <>
      <Typography variant="h4" gutterBottom>TTS Settings</Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>Settings saved successfully</Alert>}

      <Paper sx={{ p: 3, maxWidth: 600 }}>
        <FormControl fullWidth margin="dense">
          <InputLabel>TTS Engine</InputLabel>
          <Select value={engine} label="TTS Engine" onChange={e => setEngine(e.target.value)} disabled={!isAdmin}>
            <MenuItem value="piper">Piper (Offline Neural)</MenuItem>
            <MenuItem value="pyttsx3">pyttsx3 (Windows SAPI5)</MenuItem>
          </Select>
        </FormControl>

        <FormControl fullWidth margin="dense">
          <InputLabel>Voice</InputLabel>
          <Select value={voice} label="Voice" onChange={e => setVoice(e.target.value)} disabled={!isAdmin}>
            {VOICES.map(v => <MenuItem key={v.value} value={v.value}>{v.label}</MenuItem>)}
          </Select>
        </FormControl>

        <Box sx={{ mt: 2 }}>
          <Typography gutterBottom>Speech Rate: {rate.toFixed(1)}x</Typography>
          <Slider value={rate} onChange={(_, v) => setRate(v as number)} min={0.5} max={2.0} step={0.1} marks disabled={!isAdmin} />
        </Box>

        <Box sx={{ mt: 2 }}>
          <Typography gutterBottom>Pitch: {pitch.toFixed(1)}x</Typography>
          <Slider value={pitch} onChange={(_, v) => setPitch(v as number)} min={0.5} max={2.0} step={0.1} marks disabled={!isAdmin} />
        </Box>

        <Box sx={{ mt: 2 }}>
          <Typography gutterBottom>Volume: {volume.toFixed(1)}x</Typography>
          <Slider value={volume} onChange={(_, v) => setVolume(v as number)} min={0.1} max={2.0} step={0.1} marks disabled={!isAdmin} />
        </Box>

        <TextField label="Repeat Count" type="number" fullWidth margin="dense" value={repeatCount}
          onChange={e => setRepeatCount(parseInt(e.target.value) || 1)} disabled={!isAdmin}
          helperText="Number of times to repeat the TTS announcement" />

        <TextField label="Announcement Template" fullWidth margin="dense" value={template}
          onChange={e => setTemplate(e.target.value)} multiline rows={3} disabled={!isAdmin}
          helperText="Available variables: {station}, {call_type}, {address}, {city}, {units}, {narrative}, {zone}, {cad_number}" />

        <Box sx={{ mt: 3, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
          <Typography variant="subtitle2" gutterBottom>Preview Voice Settings</Typography>
          <TextField
            label="Preview Text"
            fullWidth
            margin="dense"
            value={previewText}
            onChange={e => setPreviewText(e.target.value)}
            multiline
            rows={2}
          />
          <Button
            variant="outlined"
            startIcon={<VolumeUp />}
            onClick={handlePreview}
            disabled={previewLoading || !previewText}
            sx={{ mt: 1 }}
          >
            {previewLoading ? 'Generating...' : 'Preview Voice'}
          </Button>
        </Box>

        {isAdmin && (
          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
            <Button variant="contained" startIcon={<Save />} onClick={handleSave}>Save Settings</Button>
          </Box>
        )}
      </Paper>
    </>
  );
}
